/**
 * ErrorBoundary — atrapa las excepciones de render para que una pantalla rota no deje la app en
 * blanco (sin boundary, React desmonta el árbol entero desde la raíz).
 *
 * Tiene que ser un componente de clase: es la única forma que da React de capturar errores de
 * render. No captura errores de handlers ni de promesas (fetch): esos siguen su camino normal.
 *
 * - `fallback(err, reset)`: qué mostrar en lugar de los hijos. Sin él, `ErrorFallback`.
 * - `resetKey`: si cambia mientras hay un error, se vuelve a intentar el render. El layout le
 *   pasa el pathname: navegar a otra sección limpia el error. Es mejor que `key={pathname}`,
 *   que remontaría la pantalla en cada navegación aunque no haya error.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Sin telemetría externa: la consola es el único registro.
    console.error("ErrorBoundary:", error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.reset();
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return this.props.fallback ? this.props.fallback(error, this.reset) : <ErrorFallback />;
  }
}

/**
 * Fallback por defecto. No usa el router ni ningún contexto: el boundary de la raíz está por
 * fuera de BrowserRouter y de los providers, y cubre también las rutas públicas (/r/:token,
 * /cliente), donde el que mira no tiene acceso a Activa. Por eso sólo ofrece recargar.
 */
export function ErrorFallback({ children }: { children?: ReactNode }) {
  return (
    <div role="alert" className="min-h-[50vh] flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-4">
        <p className="text-base font-semibold text-slate-800">Algo falló al mostrar esta pantalla</p>
        <p className="text-sm text-slate-500">Recargá para intentar de nuevo.</p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 h-10 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
          >
            Recargar
          </button>
          {children}
        </div>
      </div>
    </div>
  );
}
